import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  getDefinition, simulateDefinition, listSimulationLogs,
  type WorkflowDefinition, type SimulationLog,
} from '../../api/workflowApi.js';
import { sx } from './wfKit.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * WorkflowSimulatorPage — dry-run a workflow definition (UI Audit M04 C2).
 *
 * Posts a JSON simulation input to `/v1/wf/definitions/:id/simulate` and shows
 * the resolved path/result without creating a real instance, then lists recent
 * simulation logs for the definition (T057).
 */
export function WorkflowSimulatorPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [def, setDef] = useState<WorkflowDefinition | null>(null);
  const [inputText, setInputText] = useState('{\n  \n}');
  const [result, setResult] = useState<unknown>(null);
  const [logs, setLogs] = useState<SimulationLog[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadLogs = useCallback(() => {
    if (!id) return;
    listSimulationLogs(token, id).then(setLogs).catch((e) => setError((e as Error).message));
  }, [token, id]);

  useEffect(() => {
    if (!id) return;
    getDefinition(token, id).then(setDef).catch((e) => setError((e as Error).message));
    loadLogs();
  }, [token, id, loadLogs]);

  async function run(): Promise<void> {
    if (!id) return;
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(inputText) as Record<string, unknown>;
    } catch {
      setError(t('wf.simulator.invalidJson'));
      return;
    }
    setError(null); setBusy(true); setResult(null);
    try {
      const res = await simulateDefinition(token, id, parsed);
      setResult(res);
      loadLogs();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="wf-simulator" style={sx.main}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Link to="/admin/workflows" style={sx.link}>{t('wf.common.backDesigner')}</Link>
        <h1>{def ? t('wf.simulator.titleWithCode', { code: def.workflowCode }) : t('wf.simulator.title')}</h1>
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}

      <section aria-label="simulation-input" style={{ marginBottom: 16 }}>
        <label htmlFor="sim-input" style={sx.label}>{t('wf.simulator.inputLabel')}</label>
        <textarea
          id="sim-input" aria-label="sim-input" value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          rows={8}
          style={{ width: '100%', maxWidth: 640, fontFamily: 'monospace', padding: 8, border: '1px solid var(--color-border-default)', borderRadius: 6, boxSizing: 'border-box' }}
        />
        <div style={sx.actionBar}>
          <Button type="button" variant="primary" disabled={busy} aria-label="run-simulation" onClick={run}>
            {busy ? t('wf.simulator.simulating') : t('wf.simulator.run')}
          </Button>
        </div>
      </section>

      {result != null && (
        <section aria-label="simulation-result" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15 }}>{t('wf.simulator.result')}</h2>
          <pre data-testid="simulation-result" style={{ background: 'var(--color-bg-muted)', padding: 12, borderRadius: 6, overflow: 'auto', maxWidth: 640 }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </section>
      )}

      <section aria-label="simulation-logs">
        <h2 style={{ fontSize: 15 }}>{t('wf.simulator.recent')}</h2>
        {!logs ? <p>{t('common.loading')}</p> : logs.length === 0 ? (
          <p data-testid="sim-logs-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.simulator.empty')}</p>
        ) : (
          <DataTable<SimulationLog>
            ariaLabel="sim-logs-table"
            rows={logs}
            rowKey={(l) => l.simulationId}
            rowTestId={(l) => `sim-log-${l.simulationId}`}
            columns={[
              { key: 'when', header: t('wf.common.when'), render: (l) => (l.createdAt ? at(l.createdAt) : '—') },
              { key: 'status', header: t('wf.common.status'), render: (l) => l.simulationStatus },
            ]}
          />
        )}
      </section>
    </main>
  );
}
